import api from './api';

export interface Business {
  id: number;
  name: string;
  industry?: string;
  address?: string;
  city?: string;
  phone?: string;
  email?: string;
  manager_name?: string;
  active: boolean;
  timezone: string;
  created_at: string;
  total_audits?: number;
  average_score?: number;
  last_score?: number;
  assigned_users?: number;
}

export interface BusinessFilters {
  page?: number;
  limit?: number;
  search?: string;
  industry?: string;
  active?: boolean | string;
}

export interface BusinessStats {
  total_audits: number;
  completed_audits: number;
  pending_audits: number;
  average_score: number;
  average_compliance: number;
  total_assigned_users: number;
  last_audit: string | null;
  score_trend: string;
  audits_per_month?: Array<{
    month: string;
    total: number;
    average_score: number;
  }>;
  score_distribution?: Array<{
    range: string;
    count: number;
  }>;
}

export interface BusinessUser {
  id: number;
  name: string;
  email: string;
  role: string;
  active?: boolean;
  assignment_date?: string;
}

export interface BusinessAudit {
  id: number;
  form_name: string;
  user_name: string;
  start_date: string;
  end_date: string | null;
  status: string;
  global_score: number | null;
  compliance_percentage: number | null;
  synchronized?: boolean;
  business_timezone?: string;
}

const businessService = {
  // List businesses with filters
  async list(filters: BusinessFilters = {}) {
    const response = await api.get('/business', { params: filters });
    // Backend returns { businesses: [], pagination: {} }
    return {
      data: response.data.businesses || response.data.data || response.data || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get business by ID
  async getById(id: number) {
    const response = await api.get(`/business/${id}`);
    return response.data;
  },

  // Create new business
  async create(data: Partial<Business>) {
    const response = await api.post('/business', data);
    return response.data;
  },

  // Update business
  async update(id: number, data: Partial<Business>) {
    const response = await api.put(`/business/${id}`, data);
    return response.data;
  },

  // Delete business (soft delete)
  async delete(id: number) {
    const response = await api.delete(`/business/${id}`);
    return response.data;
  },

  // Get business statistics
  async getStats(id: number): Promise<BusinessStats> {
    const response = await api.get(`/business/${id}/stats`);
    return response.data;
  },

  // Get business audits
  async getAudits(id: number): Promise<BusinessAudit[]> {
    const response = await api.get(`/business/${id}/audits`);
    // Backend returns { audits: [], pagination: {} }
    return response.data.audits || response.data.data || response.data || [];
  },

  // Get users assigned to business
  async getUsers(id: number): Promise<BusinessUser[]> {
    const response = await api.get(`/business/${id}/users`);
    // Backend returns { users: [] } or direct array
    return response.data.users || response.data.data || response.data || [];
  },

  // Assign user to business
  async assignUser(businessId: number, userId: number) {
    const response = await api.post(`/business/${businessId}/users`, { user_id: userId });
    return response.data;
  },

  // Remove user from business
  async removeUser(businessId: number, userId: number) {
    const response = await api.delete(`/business/${businessId}/users/${userId}`);
    return response.data;
  },

  // Toggle business active status
  async toggleStatus(id: number) {
    const response = await api.patch(`/business/${id}/toggle-status`);
    return response.data;
  },
};

export default businessService;
